import {DataContentWrapper} from '@hconnect/common/containers'
import {useCurrentTime} from '@hconnect/common/hooks'
import {useKpiSearch} from '@domain/expert-system'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {SxProps, Theme} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback, useMemo} from 'react'

import {useSlaKpiTabValues} from '../hooks'
import {TabTypeDto} from '../types'

import {SlaKpiTabsValuesTablesContainer} from './SlaKpiTabsValuesTablesContainer'

type Props = {
  tabType?: TabTypeDto
  year: number
  cardBoxSx?: SxProps<Theme>
  isDesktop: boolean
  placeholderWrapperSx?: SxProps<Theme>
}

export const SlaKpiTabsValuesContainer = ({
  tabType,
  year,
  cardBoxSx,
  isDesktop,
  placeholderWrapperSx
}: Props) => {
  const {upmId: plantUpmId, plantId} = useUpmPlantContext()
  const timezone = useUpmPlantTimezone()
  const {data, isLoading, isError, isRefetching, refetch} = useSlaKpiTabValues({
    plantUpmId,
    tabType,
    year
  })

  const isEditable = tabType !== undefined
  const currentMonthDate = useCurrentTime(timezone, 'month')
  const previousMonthDate = useMemo(
    () => currentMonthDate.clone().subtract(1, 'month'),
    [currentMonthDate]
  )

  // whether KPI can be created if there is missing value
  const isCellCreatable = useCallback(
    (month: number, year: number) => {
      return isEditable && moment({month, year}).isBefore(currentMonthDate)
    },
    [currentMonthDate, isEditable]
  )

  // whether KPI can be edited if it already contains a value
  const isCellEditable = useCallback(
    (month: number, year: number) => {
      if (!data) {
        return false
      }

      if (!isEditable) {
        return false
      }

      return month === previousMonthDate.month() + 1 && year === previousMonthDate.year()
    },
    [isEditable, data, previousMonthDate]
  )

  // Build a lookup map from CCE KPI definition ID to display name
  const {data: kpiResponse} = useKpiSearch(plantId)
  const cceKpiNameMap = useMemo<Map<number, string>>(
    () =>
      new Map(
        (kpiResponse?.processStages ?? [])
          .flatMap((stage) => stage.definitionsWithResults)
          .map((kpi) => [kpi.definition.kpiAssignment.id, kpi.definition.displayName])
      ),
    [kpiResponse]
  )

  const allTabsSelected = !tabType

  return (
    <CardBox sx={{width: '100%', ...cardBoxSx}} data-test-id="sla-kpi-table-card">
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
      >
        {data && (
          <SlaKpiTabsValuesTablesContainer
            data={data}
            isDesktop={isDesktop}
            isCellCreatable={isCellCreatable}
            showAllHeader={allTabsSelected}
            isCellEditable={isCellEditable}
            isSubmitButtonVisible={isEditable}
            cceKpiNameMap={cceKpiNameMap}
          />
        )}
      </DataContentWrapper>
    </CardBox>
  )
}
